// DeviceCard.tsx — one lens profile on the Devices page (ARCHITECTURE.md §14):
// label, resolution/zoom, focal length and horizontal FOV, distortion terms,
// the fused uncertainty σ, scan count, last update and a sparkline of the
// focal estimate across scans. Actionable, so all three slots with the
// controls in the footer (DESIGN.md C-CARD-1); rename/reset/delete are
// delegated to the page, which owns the Modal and the confirm dialog.
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { BsArrowCounterclockwise, BsLockFill, BsPencil, BsTrash, BsUnlockFill } from 'react-icons/bs';
import { StatusBadge } from 'common';
import type { DeviceProfile } from '../api';
import { hfovDeg, usableProfile } from '../capture/profile';
import { fmtDate } from '../format';
import { FocalSparkline } from './FocalSparkline';

export interface DeviceCardProps {
  profile: DeviceProfile;
  busy?: boolean;
  onToggleLock: (p: DeviceProfile) => void;
  onRename: (p: DeviceProfile) => void;
  onReset: (p: DeviceProfile) => void;
  onDelete: (p: DeviceProfile) => void;
}

const fmtTerm = (v: number | undefined): string => (v == null || !Number.isFinite(v) ? '—' : v.toFixed(4));

export function DeviceCard({ profile: p, busy, onToggleLock, onRename, onReset, onDelete }: DeviceCardProps) {
  const calibrated = usableProfile(p);
  const n = p.intrinsics;
  const zoom = p.zoom && Math.abs(p.zoom - 1) > 1e-3 ? ` · ${p.zoom}× zoom` : '';
  return (
    <Card className="device-card">
      <Card.Header className="d-flex align-items-center gap-2">
        <span className="text-truncate flex-grow-1" title={p.label}>
          {p.label || 'Unnamed device'}
        </span>
        {p.locked && <StatusBadge tone="secondary">locked</StatusBadge>}
        <StatusBadge tone={calibrated ? 'success' : 'neutral'}>{calibrated ? 'calibrated' : 'calibrating'}</StatusBadge>
      </Card.Header>
      <Card.Body>
        {calibrated && n ? (
          <>
            <div className="measure-value">
              f {(n.fx * p.width).toFixed(0)} px · hfov {hfovDeg(n.fx).toFixed(1)}°
            </div>
            <div className="small text-body-secondary">
              {p.width}×{p.height}{zoom} · fy {(n.fy * p.height).toFixed(0)} px · c ({(n.cx * p.width).toFixed(0)}, {(n.cy * p.height).toFixed(0)})
            </div>
            <div className="small mt-1">
              k1 {fmtTerm(n.k1)} · k2 {fmtTerm(n.k2)} · p1 {fmtTerm(n.p1)} · p2 {fmtTerm(n.p2)}
            </div>
            {p.sigma && (
              <div className="small">
                σ f ±{(p.sigma.f_rel * 100).toFixed(2)}% · c ±{p.sigma.c_px.toFixed(1)} px · k1 ±{fmtTerm(p.sigma.k1)}
              </div>
            )}
          </>
        ) : (
          <div className="small text-body-secondary">
            {p.width}×{p.height}{zoom} · no accepted calibration yet — the next refined scan from this phone measures it.
          </div>
        )}
        <div className="small text-body-secondary mt-1">
          {p.n} fused · {p.history?.length ?? p.scans.length} scan{(p.history?.length ?? p.scans.length) === 1 ? '' : 's'} measured
          {p.pending && p.pending.length > 0 ? ` · ${p.pending.length} outlier${p.pending.length === 1 ? '' : 's'} pending` : ''}
          {p.updated_at ? ` · updated ${fmtDate(p.updated_at)}` : ''}
        </div>
        <FocalSparkline points={p.history ?? []} width={p.width} />
        <div className="small text-body-secondary text-truncate mt-1" title={p.key}>
          fp {p.fingerprint.slice(0, 12)}…
        </div>
      </Card.Body>
      <Card.Footer className="d-flex flex-wrap gap-1">
        <Button size="sm" variant="outline-secondary" onClick={() => onToggleLock(p)} disabled={busy} aria-pressed={p.locked}>
          {p.locked ? <BsUnlockFill className="me-1" /> : <BsLockFill className="me-1" />}
          {p.locked ? 'Unlock' : 'Lock'}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={() => onRename(p)} disabled={busy}>
          <BsPencil className="me-1" />
          Rename
        </Button>
        <Button size="sm" variant="outline-warning" onClick={() => onReset(p)} disabled={busy}>
          <BsArrowCounterclockwise className="me-1" />
          Reset
        </Button>
        <Button size="sm" variant="outline-danger" className="ms-auto" onClick={() => onDelete(p)} disabled={busy}>
          <BsTrash className="me-1" />
          Delete
        </Button>
      </Card.Footer>
    </Card>
  );
}
